/**
 * The one door every state-changing action goes through.
 *
 * Before this, each view hand-rolled its own `busy` flag around a direct API
 * call. That guards a double-click inside one tab and nothing else — and it had
 * to be remembered, correctly, at every call site. This funnel makes the
 * protection structural instead of remembered:
 *
 *   actionId     one uuid per gesture, sent as X-Action-Id. The server returns
 *                the first result for a repeat instead of acting twice. It rides
 *                the request config, so axios' 401→refresh→retry reuses the same
 *                id and the retry stays a replay rather than a second action.
 *   single-flight while a type is in flight, further calls of that type are
 *                DROPPED, not queued. Queueing a double-click just delays the
 *                second harvest; dropping it is the point.
 *   debounce     a repeat of the same type+target inside 250ms is ignored.
 *   rate limit   10 actions/sec per player, then drop with a console warning.
 *
 * The server does not trust any of this — it has its own dedup and per-player
 * lock (see backend action.service.ts). This layer exists so the UI stops
 * *sending* nonsense, not so the server can relax.
 */

/** Header the server reads the gesture's actionId from (see action-id.decorator.ts). */
export const ACTION_ID_HEADER = 'X-Action-Id';

export interface DispatchOptions {
  /** Distinguishes targets within one type, e.g. the plot index. Debounce is per type+key. */
  key?: string;
  /** Skip the debounce for actions the player legitimately repeats fast (e.g. a hold-to-chop swing). */
  noDebounce?: boolean;
}

const DEBOUNCE_MS = 250;
const RATE_LIMIT = 10; // actions/sec
const RATE_WINDOW_MS = 1000;

const inFlight = new Set<string>();
const lastFired = new Map<string, number>();
let recent: number[] = [];

/** Raised when the funnel refuses to send. Callers treat it as "nothing happened". */
export class ActionDropped extends Error {
  constructor(readonly reason: 'in-flight' | 'debounced' | 'rate-limited') {
    super(`action dropped: ${reason}`);
    this.name = 'ActionDropped';
  }
}

export const isDropped = (e: unknown): e is ActionDropped => e instanceof ActionDropped;

function newActionId(): string {
  const c = globalThis.crypto;
  if (c && 'randomUUID' in c) return c.randomUUID();
  return `a-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Test seam: reset the funnel's memory between cases. */
export function __resetDispatch(): void {
  inFlight.clear();
  lastFired.clear();
  recent = [];
}

/**
 * Run `fn` under the funnel. `fn` receives the actionId to stamp on its request.
 *
 * Throws ActionDropped when the call was refused — that is not a failure, it is
 * the guard doing its job, so callers should swallow it rather than toast it.
 */
export async function dispatchAction<T>(
  type: string,
  fn: (actionId: string) => Promise<T>,
  opts: DispatchOptions = {},
  now: number = Date.now(),
): Promise<T> {
  const slot = opts.key ? `${type}:${opts.key}` : type;

  if (inFlight.has(slot)) throw new ActionDropped('in-flight');

  if (!opts.noDebounce) {
    const prev = lastFired.get(slot);
    if (prev !== undefined && now - prev < DEBOUNCE_MS) throw new ActionDropped('debounced');
  }

  recent = recent.filter((t) => now - t < RATE_WINDOW_MS);
  if (recent.length >= RATE_LIMIT) {
    console.warn(`[action] rate limit: dropped "${type}" (${RATE_LIMIT}/s)`);
    throw new ActionDropped('rate-limited');
  }

  inFlight.add(slot);
  lastFired.set(slot, now);
  recent.push(now);
  try {
    const result = await fn(newActionId());
    notify(type);   // success → let the tutorial coach advance
    return result;
  } finally {
    inFlight.delete(slot);
  }
}

/** True while an action of this type (and key) is awaiting its response. */
export const isBusy = (type: string, key?: string): boolean => inFlight.has(key ? `${type}:${key}` : type);

// ─── Action observers (tutorial coach) ───────────────────────────────────────
// A de-facto event stream: every action that completes SUCCESSFULLY through the
// funnel notifies these listeners with its type string ('wood.chop', 'field.till',
// …). The tutorial uses this to auto-advance its steps without instrumenting each
// call site. Listeners must never throw into the funnel — we isolate them.
type ActionObserver = (type: string) => void;
const observers = new Set<ActionObserver>();

/** Subscribe to successful actions. Returns an unsubscribe fn. */
export function onAction(fn: ActionObserver): () => void {
  observers.add(fn);
  return () => observers.delete(fn);
}

function notify(type: string): void {
  for (const fn of observers) { try { fn(type); } catch { /* a coach bug must not break gameplay */ } }
}
